// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import { TFunction } from 'i18next';
import { GridColDef } from '@mui/x-data-grid-pro';

// import utils
import { dateFormatter, nestedFieldValueGetter } from 'utils/grid';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

type Row = {
  flags: string[];
  [key: string]: any;
};

const useColumnDefinitions = (): GridColDef[] => {
  const { t } = useTranslation('common');

  return useMemo(() => {
    return columnDefinitions(t);
  }, [t]);
};

const columnDefinitions = (t: TFunction): GridColDef[] => [
  {
    field: 'code',
    flex: 1,
    headerName: t('costCenterDetail.code')
  },
  {
    field: 'name',
    flex: 1,
    headerName: t('costCenterDetail.name')
  },
  {
    field: 'nameShort',
    flex: 1,
    headerName: t('costCenterDetail.shortName')
  },
  {
    field: 'codeExt',
    flex: 1,
    headerName: t('costCenterDetail.exportCode')
  },
  {
    field: 'codeExtSecondary',
    flex: 1,
    headerName: t('costCenterDetail.exportCode2')
  },
  {
    field: 'codeImp',
    flex: 1,
    headerName: t('costCenterDetail.importCode')
  },
  {
    field: 'codeImpSecondary',
    flex: 1,
    headerName: t('costCenterDetail.importCode2')
  },
  {
    field: 'benefitStartDate',
    flex: 1,
    headerName: t('costCenterDetail.importStartDate'),
    valueGetter: (_, row: Row) =>
      nestedFieldValueGetter('benefitStartDate', row),
    valueFormatter: dateFormatter
  },
  {
    field: 'financialBrNameShort',
    flex: 1,
    headerName: t('costCenterDetail.financialCostCenter')
  },
  {
    field: 'parentBrNameShort',
    flex: 1,
    headerName: t('costCenterDetail.parentCostCenter')
  },
  {
    field: 'flags.BUH_BLOCKED',
    flex: 1,
    headerName: t('costCenterDetail.noBUH'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: (_, row: Row) =>
      nestedFieldValueGetter('flags.BUH_BLOCKED', row)
  },
  {
    field: 'flags.PLANNING_EXPORT',
    flex: 1,
    headerName: t('costCenterDetail.excludeFromExport'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: (_, row: Row) =>
      nestedFieldValueGetter('flags.PLANNING_EXPORT', row)
  },
  {
    field: 'flags.VAT_DEDUCTIBLE',
    flex: 1,
    headerName: t('costCenterDetail.VATDeductible'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: (_, row: Row) =>
      nestedFieldValueGetter('flags.VAT_DEDUCTIBLE', row)
  },
  {
    field: 'flags.INVOICE_DEBIT_BLOCKED',
    flex: 1,
    headerName: t('costCenterDetail.excludeBorrowFromInvoice'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: (_, row: Row) =>
      nestedFieldValueGetter('flags.INVOICE_DEBIT_BLOCKED', row)
  },
  {
    field: 'flags.INVOICE_CREDIT_BLOCKED',
    flex: 1,
    headerName: t('costCenterDetail.excludeLendFromInvoice'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: (_, row: Row) =>
      nestedFieldValueGetter('flags.INVOICE_CREDIT_BLOCKED', row)
  },
  {
    field: 'valid.from',
    flex: 1,
    headerName: t('validFrom'),
    valueGetter: (_, row: Row) => nestedFieldValueGetter('valid.from', row),
    valueFormatter: dateFormatter
  },
  {
    field: 'valid.to',
    flex: 1,
    headerName: t('validTo'),
    type: 'date',
    valueGetter: (_, row: Row) => nestedFieldValueGetter('valid.to', row),
    valueFormatter: dateFormatter
  },
  {
    field: 'employerName',
    flex: 1,
    headerName: t('costCenterDetail.employer')
  }
];

export default useColumnDefinitions;
